import { faSignOut } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Container, Grid, Paper } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthFlow } from '../feature/auth/auth-flow';
import { useAuth } from '../feature/auth/auth-provider';
import { QuotationTable } from '../feature/ecommerce/quotations/quotation-table';
import { AccountForm } from '../feature/ecommerce/user-settings/account';
import { AddressForm } from '../feature/ecommerce/user-settings/address';
import { useEcommerceSettings } from '../feature/ecommerce/user-settings/ecommerce-settings-provider';
import { PageHeader } from '../feature/page-layout/header/page-header';
import { Page } from '../feature/page-layout/page';
import { PentairHeading } from '../theme/components/pentair-heading';

export const PageUserAccount = () => {
  const { t } = useTranslation();

  const {
    user,
    isAuthenticated,
    awaitingSignedInUser,
    signOut,
    updateUserAttributes,
    loading: userLoading,
  } = useAuth();

  const { settings, updateAddress, loading: settingsLoading } = useEcommerceSettings().user;

  const saveUserAttributes = useCallback(
    (attributes: any) => updateUserAttributes(attributes as Record<string, string>),
    [updateUserAttributes]
  );

  if (awaitingSignedInUser) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Box
        flex={1}
        minHeight={['100%', '100vh']}
        paddingTop={['', '72px', '']}
        paddingBottom={['none', '72px', 'none']}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor={['grey.200']}
      >
        <Paper>
          <AuthFlow />
        </Paper>
      </Box>
    );
  }

  return (
    <Page
      title={t('app:pageAccount.SEOTitle', { defaultValue: 'mySüdmo - My account' })}
      description={t('app:pageAccount.SEODescription', {
        defaultValue: 'Manage your account, address and requested quotations',
      })}
    >
      <Container
        sx={{
          // paddingY: [0, 0, '3vw'],
          maxWidth: '1920px !important',
          paddingLeft: '5vw !important',
          paddingRight: '5vw !important',
          paddingBottom: '5vw',
          paddingTop: 'calc(48px + 3vw)',
        }}
      >
        <Box>
          <PageHeader
            primaryText={t('app:pageAccount.title', { defaultValue: 'my' })}
            secondaryText={t('app:pageAccount.subTitle', { defaultValue: 'account' })}
            actions={
              <Button onClick={() => signOut()} endIcon={<FontAwesomeIcon icon={faSignOut} />}>
                {t('iam:buttonLabel.signOut', { defaultValue: 'Sign out' })}
              </Button>
            }
          />
        </Box>
        <Box>
          <Grid container spacing={[0, 0, 2]}>
            <Grid item xs={12} md={6}>
              <PentairHeading>
                {t('app:pageAccount.heading.yourInformation', { defaultValue: 'Your information' })}
              </PentairHeading>
              <AccountForm initialRecord={user?.attributes} onSubmit={saveUserAttributes} disabled={userLoading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <PentairHeading>{t('app:pageAccount.heading.address', { defaultValue: 'Address' })}</PentairHeading>
              <AddressForm
                initialRecord={settings?.addresses?.[0]}
                onSubmit={updateAddress}
                disabled={settingsLoading}
              />
            </Grid>
          </Grid>

          {/* It seems too close to the top: This might fix it paddingTop={'calc(48px + 3vw)'} */}
          <PentairHeading marginTop={'calc(48px + 3vw)'}>
            {t('app:pageAccount.heading.requestedQuotations', { defaultValue: 'Requested Quotations' })}
          </PentairHeading>

          <QuotationTable />
        </Box>
      </Container>
    </Page>
  );
};

export default PageUserAccount;
